import { useEffect, useRef, useState } from "react";
import Lock from "@assets/images/mdi_lock.svg";
import { ChevronDown } from "lucide-react";
import { Checkbox } from "@mui/material";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

const ExercisePlanMultiSelectDropdown = ({
  options, // This should be array of IDs now
  selectedValues, // This should be array of IDs
  onSelect,
  placeholder,
  isEditable = true,
  isFistForm = false,
  label = "",
  hasError = false,
  sleepCoach = false,
  maxSelection = null,
  fieldKey = null,
  displayMap = null,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const dropdownRef = useRef(null);
  const { t } = useTranslation();

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsOpen(false);
      setSearchTerm("");
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleOption = (optionId) => {
    if (selectedValues.includes(optionId)) {
      onSelect(selectedValues.filter((item) => item !== optionId));
    } else {
      if (maxSelection && selectedValues.length >= maxSelection) {
        toast.error(`The field cannot have more than ${maxSelection} values.`);
        return;
      }
      onSelect([...selectedValues, optionId]);
    }
  };

  // Get display name for an ID
  const getDisplayName = (id) => {
    if (!displayMap) return id; // Fallback to ID if no displayMap provided
    return displayMap[id] || id; // Return display name or fallback to ID
  };

  // Filter options based on search term (search in display names)
  const filteredOptions = options.filter((optionId) => {
    const displayName = getDisplayName(optionId);
    return displayName.toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Get the display value for the placeholder/selected display
  const getSelectedDisplayValue = () => {
    if (selectedValues.length === 0) return placeholder;

    // Show the last selected item's display name
    const lastSelectedId = selectedValues[selectedValues.length - 1];
    return getDisplayName(lastSelectedId);
  };

  return (
    <div
      className={`relative ${isFistForm ? "w-full" : "w-[90%]"}`}
      ref={dropdownRef}
    >
      <div
        className={`border p-2 pr-[0.6rem] rounded-md w-full h-10 flex items-center justify-between bg-[#fff] ${
          !isEditable
            ? "pointer-events-none opacity-70 bg-[rgba(0, 0, 0, 0.38)]"
            : " cursor-pointer"
        } ${
          hasError ? "border-red-500 placeholder-red-400" : "border-gray-300"
        }`}
        onClick={() => {
          if (!isEditable) return;
          if (isOpen) {
            setSearchTerm("");
          }
          setIsOpen((prev) => !prev);
        }}
      >
        <span
          className={`truncate text-[12px] ${
            hasError && selectedValues.length === 0
              ? !isFistForm && "text-red-400"
              : "text-black"
          }`}
        >
          {getSelectedDisplayValue()}
        </span>
        <ChevronDown
          className="w-[14px] h-[14px]"
          color="black"
          strokeWidth={4}
        />
      </div>

      {isOpen && isEditable && (
        <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-[300px] overflow-y-auto">
          {/* Search Input */}
          <div className="px-3 py-2 border-b">
            <input
              type="text"
              placeholder={`${t("search")}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm focus:outline-none"
            />
          </div>

          {/* Filtered Options */}
          {filteredOptions.length > 0 ? (
            filteredOptions.map((optionId) => {
              const displayName = getDisplayName(optionId);
              return (
                <div
                  key={optionId}
                  className={`px-4 py-2 hover:bg-blue-50 cursor-pointer ${
                    selectedValues.includes(optionId)
                      ? "bg-blue-100"
                      : "bg-white"
                  }`}
                  onClick={() => toggleOption(optionId)}
                >
                  <div className="flex items-center gap-2">
                    <Checkbox
                      checked={selectedValues.includes(optionId)}
                      readOnly
                      color="primary"
                      sx={{
                        padding: "0",
                      }}
                    />
                    <span>{displayName}</span>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="px-4 py-2 text-sm text-gray-500">
              No options found.
            </div>
          )}
        </div>
      )}

      {hasError && (
        <div className="text-red-500 text-[10px] md:text-[12px] ml-2 mt-1">
          {sleepCoach
            ? "This field is required"
            : t("validation.required", {
                field: t(`firstFormSteps.${[fieldKey]}.label`),
              })}
        </div>
      )}
    </div>
  );
};

export default ExercisePlanMultiSelectDropdown;
